// Core packages

// Third party packages
import moment from "moment";

// Custom packages
import { labelOf } from "src/app/modules/properties/document-archive-insert.utils";
import {
  archiveAttachedFiles,
  archiveRowStatusClass,
  canActOnArchiveContract,
  canDeleteArchiveDocument,
  canEditArchiveDocumentData,
  canExpireArchiveContract,
} from "src/app/modules/properties/document-archive-row-actions.utils";
import {
  archiveDocumentDay,
  archiveSearchDay,
  escapeArchiveSearchText,
} from "src/app/modules/properties/document-archive-search.utils";
import { isGuestRoleType } from "src/app/modules/properties/document-archive-visibility.utils";
import { DocumentArchiveDocListItem } from "src/app/shared/interfaces/document-archive-doc.interface";
import {
  ARCHIVE_SEARCH_ALL_PROPERTIES,
  ARCHIVE_SEARCH_MAX_LIST,
  ARCHIVE_SEARCH_MAX_TEXT,
  DocumentArchiveSearchParams,
  DocumentArchiveSearchRow,
  DocumentArchiveSearchTaxonomy,
  DocumentArchiveSearchTaxonomyArea,
  DocumentArchiveSearchTaxonomyContent,
} from "src/app/shared/interfaces/document-archive-search.interface";

/**
 * Pure helpers of the GENERAL search on the 3.0.0 archive (`/search-docs/listv3`): the lists of the
 * filters, the query string sent to `GET /document-archive-docs/search`, the table of results, its
 * export and the menu of a row.
 *
 * The page is the successor of the legacy general search (`search-docs-listv2`), with its layout and
 * its rules. Five bugs of that page are NOT carried over:
 *  - typing in "Cerca per nome" or "Cerca per codice" rebuilt the list of the properties as a copy, so
 *    the ticks were lost while typing: here the ticks live on the options themselves;
 *  - sorting by "Data di scadenza" never worked (the column and the sort key had different names);
 *  - the comparison of the sort never answered "equal", so equal rows were shuffled;
 *  - the text typed in "Cerca documento" was used as a regular expression without escaping it;
 *  - the supplier was sent by NAME: the new back-end wants the id of the suppliers back-end.
 *
 * Nothing here calls the back-end.
 *
 * @since 3.0.0
 */

/**
 * Role types that only DOWNLOAD from the results, as in the legacy page (`loggedUserMaster`): no link
 * to the property or to its archive, no data, no delete, no contract action.
 */
const DOWNLOAD_ONLY_ROLE_TYPES = ["master", "collaborator"];

/** Role types of the platform staff: the only ones the back-end reads `company` for */
const PLATFORM_ROLE_TYPES = ["admin", "operator"];

/** An id as the back-end writes it: 24 hexadecimal digits */
const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;

/**
 * Longest query string the page sends, in characters.
 *
 * Every id costs between 36 (`properties=<id>&`) and 48 (`documentTemplateDocIds=<id>&`) characters,
 * and the address does not travel freely: nginx keeps the request line within 8 KB by default
 * (`large_client_header_buffers`, not verified on the server) and Node keeps the request line and the
 * headers within 16 KB. Past them the answer is a 414 or a 431, which a proxy can send without the
 * CORS headers, and the browser then reports it as a network failure. Refusing here, with a message
 * that says what to do, is the only way to be sure the user reads the right reason.
 */
export const GENERAL_SEARCH_MAX_QUERY_LENGTH = 7000;

/** Seconds to wait after a 429 when the answer does not say: the block of the back-end */
export const GENERAL_SEARCH_DEFAULT_RETRY_SECONDS = 60;

/** Excel font colour of a mandatory document filed: green, as in the legacy export */
export const GENERAL_SEARCH_EXCEL_GREEN = "FF28a745";

/** Excel font colour of a mandatory document missing: red, as in the legacy export */
export const GENERAL_SEARCH_EXCEL_RED = "FFdc3545";

/**
 * A property of the column "Immobili".
 *
 * `checked` is bound to its checkbox, so the object must stay the same while the list is filtered:
 * that is what keeps a tick while the user types in "Cerca per nome".
 *
 * @since 3.0.0
 */
export interface GeneralSearchPropertyOption {
  id: string;
  name: string;
  masterCode: string;
  checked: boolean;
}

/**
 * An entry of "Argomento": an AREA of the new taxonomy.
 *
 * @since 3.0.0
 */
export interface GeneralSearchAreaOption {
  id: string;
  label: string;
  checked: boolean;
}

/**
 * An entry of "Sottoargomento": a SECTION of the new taxonomy, under the area it belongs to.
 *
 * @since 3.0.0
 */
export interface GeneralSearchSectionOption {
  id: string;
  label: string;
  /** The area the section belongs to: the heading it is listed under */
  parentId: string;
  parentLabel: string;
  checked: boolean;
}

/**
 * An entry of "Documenti": a DOCUMENT TYPE of the new taxonomy, under the first ticked section that
 * holds it.
 *
 * ONE ENTRY PER TYPE: the section of «Contratti forniture e servizi» lists the contract types of
 * every other section with the same id, so with both sections ticked the same type would otherwise be
 * offered twice, with two ticks for one filter.
 *
 * @since 3.0.0
 */
export interface GeneralSearchDocOption {
  id: string;
  label: string;
  /** The section the type is listed under */
  parentId: string;
  parentLabel: string;
  /** The obligation of the type: what "Selezionare solo gli obbligatori" keeps */
  mandatory: boolean;
  /** Written in red: mandatory, or one of the two types the legacy highlights (PA31, PA32) */
  red: boolean;
  checked: boolean;
  /** Whether the filters of the column ("solo obbligatori", "Cerca documento") show it */
  visible: boolean;
}

/**
 * Options listed under one heading.
 *
 * @since 3.0.0
 */
export interface GeneralSearchOptionGroup<T> {
  id: string;
  heading: string;
  options: T[];
}

/**
 * Everything the user chose in the filters, as the page holds it.
 *
 * @since 3.0.0
 */
export interface GeneralSearchSelection {
  /** The WHOLE list of the properties, filtered or not */
  properties: GeneralSearchPropertyOption[];
  /** Text of "Cerca per nome" */
  propertyNameFilter: string;
  /** Text of "Cerca per codice" */
  propertyCodeFilter: string;
  /** Type of the active role */
  roleType: string;
  /** Company chosen in "Filtra per società", `""` for none */
  companyId: string;
  /** Group chosen in "Area" (`amministrativo`, `contratti`, ...), `""` for none */
  group: string;
  /** The areas of that group */
  areas: GeneralSearchAreaOption[];
  /** The sections of the ticked areas */
  sections: GeneralSearchSectionOption[];
  /** The document types of the ticked sections */
  docs: GeneralSearchDocOption[];
  /** Whether "Selezionare solo gli obbligatori" or "Cerca documento" narrows the documents shown */
  docFilterActive: boolean;
  /** Supplier chosen: an id of the SUPPLIERS back-end, `""` for none */
  supplierId: string;
  /** Value of "Dal" and "Al": a moment, a Date, a text or nothing */
  dateFrom: unknown;
  dateTo: unknown;
  /** Text of "Protocollo" and "Parola chiave", as typed */
  protocol: string;
  keyword: string;
}

/**
 * Why a search is not sent:
 *  - `noProperty`: no property ticked among those shown;
 *  - `dateOrder`: "Dal" comes after "Al";
 *  - `textTooLong`: "Protocollo" or "Parola chiave" longer than the back-end accepts;
 *  - `tooManyItems`: too many ids for one address (@see GENERAL_SEARCH_MAX_QUERY_LENGTH).
 *
 * @since 3.0.0
 */
export type GeneralSearchRefusal = "noProperty" | "dateOrder" | "textTooLong" | "tooManyItems";

/**
 * The query string of a search, or why it is not sent.
 *
 * @since 3.0.0
 */
export type GeneralSearchParamsOutcome =
  | { ok: true; params: DocumentArchiveSearchParams }
  | { ok: false; reason: GeneralSearchRefusal };

/**
 * What a failed search asks of the user. `other` is left to the generic error toast.
 *
 * @since 3.0.0
 */
export type GeneralSearchErrorKind =
  | "tooManyResults"
  | "invalidParameters"
  | "forbidden"
  | "rateLimited"
  | "tooManyItems"
  | "network"
  | "other";

/**
 * A row of the table of results, with what its columns print computed once: the table can hold
 * 20.000 rows, and sorting or exporting them must not recompute labels and dates at every comparison.
 *
 * @since 3.0.0
 */
export interface GeneralSearchResultRow {
  /** The row as the back-end sent it: what the menu acts on */
  row: DocumentArchiveSearchRow;
  /** Position in the answer of the back-end: the order with no sort, and the tie-break of every sort */
  order: number;
  propertyId: string;
  propertyName: string;
  masterCode: string;
  /** Label of the document type, in the language of the page */
  typeLabel: string;
  /** "Impianto": the element the row hangs from */
  structure: string;
  /** "Contenuto": `data.contenutoDocumento` */
  content: string;
  /** "Nome esecutore": the name the back-end resolved */
  executor: string;
  /** Id of that supplier in the SUPPLIERS back-end, `""` when the row names none */
  executorSupplierId: string;
  /** The date of the document as stored, for the date pipe of the table */
  documentDate: unknown;
  /** Its Rome calendar day, `YYYY-MM-DD`, or null when it is no date */
  documentDay: string | null;
  /** True when the document was archived with "data non applicabile" */
  documentDateNotApplicable: boolean;
  /** Rome calendar day of the expiry, or null */
  expiryDay: string | null;
  /** "Codice": the code the back-end reads off the barcode, `""` when there is none */
  code: string;
  /** CSS class of the colour of the row */
  statusClass: string;
}

/** The columns the table can be sorted by */
export type GeneralSearchSortKey =
  | "property"
  | "masterCode"
  | "docType"
  | "structure"
  | "content"
  | "reference"
  | "documentDate"
  | "expirationDate"
  | "code";

/** @see GeneralSearchSortKey */
const SORT_KEYS: string[] = [
  "property",
  "masterCode",
  "docType",
  "structure",
  "content",
  "reference",
  "documentDate",
  "expirationDate",
  "code",
];

/**
 * The entries of the menu of a row, entry by entry.
 *
 * @since 3.0.0
 */
export interface GeneralSearchRowMenu {
  /** "Archivio": the archive of the property, in a new tab */
  archive: boolean;
  /** "Visualizza dati aggiuntivi" */
  show: boolean;
  /** "Modifica dati aggiuntivi" */
  edit: boolean;
  /** "Scarica file" */
  download: boolean;
  /** "Elimina documento" */
  remove: boolean;
  /** "Invia disdetta" */
  cancel: boolean;
  /** "Disattiva il contratto" */
  expire: boolean;
}

/**
 * The entries that need the tree of the property of the row: every one but "Archivio" and the
 * download.
 *
 * @since 3.0.0
 */
export type GeneralSearchTreeAction = "show" | "edit" | "remove" | "cancel" | "expire";

/**
 * One line of the Excel export: its cells, and the colour of its font (`null` for black).
 *
 * @since 3.0.0
 */
export interface GeneralSearchExcelLine {
  cells: Array<string | undefined>;
  color: string | null;
}

/**
 * Whether a value is an id as the back-end writes it.
 *
 * @since 3.0.0
 *
 * @param value The value
 */
export function isArchiveObjectId(value: unknown): value is string {
  return typeof value === "string" && OBJECT_ID_PATTERN.test(value);
}

/**
 * Whether the role is platform staff (`admin`, `operator`): the only roles offered "Filtra per
 * società", because the back-end reads the company for them alone.
 *
 * @since 3.0.0
 *
 * @param roleType Type of the active role
 */
export function isGeneralSearchPlatformRole(roleType: string): boolean {
  return typeof roleType === "string" && PLATFORM_ROLE_TYPES.indexOf(roleType) > -1;
}

/**
 * Whether the role only downloads from the results (`master`, `collaborator`), as in the legacy page.
 *
 * @since 3.0.0
 *
 * @param roleType Type of the active role
 */
export function isGeneralSearchDownloadOnlyRole(roleType: string): boolean {
  return typeof roleType === "string" && DOWNLOAD_ONLY_ROLE_TYPES.indexOf(roleType) > -1;
}

/**
 * Distinct values, in the order they first appear.
 *
 * @param values The values
 */
function distinct(values: string[]): string[] {
  const seen: { [value: string]: boolean } = Object.create(null);
  const result: string[] = [];
  values.forEach((value) => {
    if (!seen[value]) {
      seen[value] = true;
      result.push(value);
    }
  });
  return result;
}

/**
 * A text, or `""` for anything that is not one.
 *
 * @param value The value
 */
function textOf(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * The comparison of the labels and of the sorted columns: the order of the language of the page,
 * numbers in numeric order ("Scala 2" before "Scala 10"), case and accents ignored.
 *
 * One collator per list rather than `localeCompare` per comparison: sorting 20.000 rows calls it
 * hundreds of thousands of times, and `localeCompare` with options builds a collator at every call.
 *
 * @param lang Language of the page
 */
function comparatorFor(lang: string): (a: string, b: string) => number {
  try {
    const collator = new Intl.Collator(lang === "en" ? "en" : "it", {
      numeric: true,
      sensitivity: "base",
    });
    return (a: string, b: string): number => collator.compare(a, b);
  } catch (err) {
    // RangeError from a runtime without the locale data: the plain order of the characters
    return (a: string, b: string): number => (a === b ? 0 : a < b ? -1 : 1);
  }
}

/**
 * Label of a node of the taxonomy: the language of the page, then Italian, then its title.
 *
 * @param node The node
 * @param lang Language of the page
 */
function nodeLabel(
  node: { title?: unknown; translation?: unknown } | null | undefined,
  lang: string
): string {
  if (!node) {
    return "";
  }
  const translation =
    node.translation && typeof node.translation === "object"
      ? (node.translation as { it: string; en: string })
      : null;
  const label = labelOf(translation, lang);
  return label ? label : textOf(node.title);
}

/**
 * The areas of the taxonomy, or none when it is not one.
 *
 * @param taxonomy The taxonomy
 */
function taxonomyAreas(
  taxonomy: DocumentArchiveSearchTaxonomy | null | undefined
): DocumentArchiveSearchTaxonomyArea[] {
  return taxonomy && Array.isArray(taxonomy.areas) ? taxonomy.areas : [];
}

/**
 * The list of the properties, from the answer of `properties/get-all` (`SearchDocsService
 * .getSearchDocsProperties`), in the order it came in. Every property starts unticked.
 *
 * Entries without an id are left out, and an id met twice is listed once.
 *
 * @since 3.0.0
 *
 * @param res The answer
 * @returns The options, or null when the answer is not a list
 */
export function generalSearchPropertyOptions(res: unknown): GeneralSearchPropertyOption[] | null {
  if (!res || typeof res !== "object" || !(res as { status?: unknown }).status) {
    return null;
  }
  const data = (res as { data?: unknown }).data;
  const items =
    data && typeof data === "object" && Array.isArray((data as { items?: unknown }).items)
      ? ((data as { items: unknown[] }).items)
      : Array.isArray(data)
      ? (data as unknown[])
      : null;
  if (!items) {
    return null;
  }

  const seen: { [id: string]: boolean } = Object.create(null);
  const options: GeneralSearchPropertyOption[] = [];
  items.forEach((item: unknown) => {
    if (!item || typeof item !== "object") {
      return;
    }
    const source = item as { _id?: unknown; name?: unknown; masterCode?: unknown };
    if (!isArchiveObjectId(source._id) || seen[source._id]) {
      return;
    }
    seen[source._id] = true;
    options.push({
      id: source._id,
      name: textOf(source.name),
      masterCode: textOf(source.masterCode),
      checked: false,
    });
  });
  return options;
}

/**
 * Whether "Cerca per nome" or "Cerca per codice" narrows the list of the properties.
 *
 * @since 3.0.0
 *
 * @param nameText Text of "Cerca per nome"
 * @param codeText Text of "Cerca per codice"
 */
export function isPropertyFilterActive(nameText: string, codeText: string): boolean {
  return textOf(nameText).trim() !== "" || textOf(codeText).trim() !== "";
}

/**
 * The properties shown: the name contains the text of "Cerca per nome" and the master code the text
 * of "Cerca per codice", case ignored, as in the legacy.
 *
 * The SAME objects as the list, never copies: their ticks survive the filter.
 *
 * @since 3.0.0
 *
 * @param options  The whole list
 * @param nameText Text of "Cerca per nome"
 * @param codeText Text of "Cerca per codice"
 */
export function shownPropertyOptions(
  options: GeneralSearchPropertyOption[],
  nameText: string,
  codeText: string
): GeneralSearchPropertyOption[] {
  const list = Array.isArray(options) ? options : [];
  const name = textOf(nameText).trim().toLowerCase();
  const code = textOf(codeText).trim().toLowerCase();
  if (!name && !code) {
    return list;
  }
  return list.filter(
    (option) =>
      option.name.toLowerCase().indexOf(name) !== -1 &&
      option.masterCode.toLowerCase().indexOf(code) !== -1
  );
}

/**
 * Whether every option is ticked: the state of a "Seleziona tutti". False on an empty list.
 *
 * @since 3.0.0
 *
 * @param options The options shown
 */
export function areAllTicked(options: Array<{ checked: boolean }>): boolean {
  return Array.isArray(options) && options.length > 0 && options.every((option) => !!option.checked);
}

/**
 * Whether at least one option is ticked.
 *
 * @since 3.0.0
 *
 * @param options The options
 */
export function isAnyTicked(options: Array<{ checked: boolean }>): boolean {
  return Array.isArray(options) && options.some((option) => !!option.checked);
}

/**
 * The groups of "Area" the taxonomy actually has, in the order of the application
 * (`ConfigService.availableDocumentAreas`). A group the application does not know has no label and is
 * not offered, as in the legacy.
 *
 * @since 3.0.0
 *
 * @param taxonomy    The taxonomy
 * @param knownGroups The groups of the application, in their order
 */
export function generalSearchGroups(
  taxonomy: DocumentArchiveSearchTaxonomy | null | undefined,
  knownGroups: string[]
): string[] {
  const present: { [group: string]: boolean } = Object.create(null);
  taxonomyAreas(taxonomy).forEach((area) => {
    if (area && typeof area.group === "string") {
      present[area.group] = true;
    }
  });
  return (Array.isArray(knownGroups) ? knownGroups : []).filter((group) => !!present[group]);
}

/**
 * The entries of "Argomento" for a group: its areas, in alphabetical order as in the legacy, none
 * ticked.
 *
 * @since 3.0.0
 *
 * @param taxonomy The taxonomy
 * @param group    The group chosen in "Area"
 * @param lang     Language of the page
 */
export function generalSearchAreaOptions(
  taxonomy: DocumentArchiveSearchTaxonomy | null | undefined,
  group: string,
  lang: string
): GeneralSearchAreaOption[] {
  if (!group) {
    return [];
  }
  const compare = comparatorFor(lang);
  const seen: { [id: string]: boolean } = Object.create(null);
  const options: GeneralSearchAreaOption[] = [];
  taxonomyAreas(taxonomy).forEach((area) => {
    if (!area || area.group !== group || !isArchiveObjectId(area._id) || seen[area._id]) {
      return;
    }
    seen[area._id] = true;
    options.push({ id: area._id, label: nodeLabel(area, lang), checked: false });
  });
  return options.sort((a, b) => compare(a.label, b.label));
}

/**
 * Whether an option of the previous list was ticked, by id.
 *
 * @param previous The previous list
 */
function tickedIdsOf(previous: Array<{ id: string; checked: boolean }>): { [id: string]: boolean } {
  const ticked: { [id: string]: boolean } = Object.create(null);
  (Array.isArray(previous) ? previous : []).forEach((option) => {
    if (option && option.checked) {
      ticked[option.id] = true;
    }
  });
  return ticked;
}

/**
 * The entries of "Sottoargomento": the sections of the ticked areas, area by area in the order of
 * "Argomento", each area's sections in alphabetical order.
 *
 * A section already ticked stays ticked; one whose area is no longer ticked leaves the list, and
 * comes back unticked, as in the legacy.
 *
 * @since 3.0.0
 *
 * @param taxonomy The taxonomy
 * @param areas    The entries of "Argomento"
 * @param lang     Language of the page
 * @param previous The entries before the change
 */
export function generalSearchSectionOptions(
  taxonomy: DocumentArchiveSearchTaxonomy | null | undefined,
  areas: GeneralSearchAreaOption[],
  lang: string,
  previous: GeneralSearchSectionOption[]
): GeneralSearchSectionOption[] {
  const compare = comparatorFor(lang);
  const ticked = tickedIdsOf(previous);
  const byId: { [id: string]: DocumentArchiveSearchTaxonomyArea } = Object.create(null);
  taxonomyAreas(taxonomy).forEach((area) => {
    if (area && isArchiveObjectId(area._id)) {
      byId[area._id] = area;
    }
  });

  const seen: { [id: string]: boolean } = Object.create(null);
  const options: GeneralSearchSectionOption[] = [];
  (Array.isArray(areas) ? areas : []).forEach((areaOption) => {
    const area = areaOption && areaOption.checked ? byId[areaOption.id] : undefined;
    if (!area) {
      return;
    }
    const contents = Array.isArray(area.contents) ? area.contents : [];
    const sections: GeneralSearchSectionOption[] = [];
    contents.forEach((content) => {
      if (!content || !isArchiveObjectId(content._id) || seen[content._id]) {
        return;
      }
      seen[content._id] = true;
      sections.push({
        id: content._id,
        label: nodeLabel(content, lang),
        parentId: areaOption.id,
        parentLabel: areaOption.label,
        checked: !!ticked[content._id],
      });
    });
    sections.sort((a, b) => compare(a.label, b.label));
    sections.forEach((section) => options.push(section));
  });
  return options;
}

/**
 * The entries of "Documenti": the document types of the ticked sections, section by section in the
 * order of "Sottoargomento", each section's types in alphabetical order, ONE ENTRY PER TYPE
 * (@see GeneralSearchDocOption). A type already ticked stays ticked. Every entry starts visible: the
 * filters of the column are applied afterwards (@see applyGeneralSearchDocFilters).
 *
 * @since 3.0.0
 *
 * @param taxonomy The taxonomy
 * @param sections The entries of "Sottoargomento"
 * @param lang     Language of the page
 * @param previous The entries before the change
 */
export function generalSearchDocOptions(
  taxonomy: DocumentArchiveSearchTaxonomy | null | undefined,
  sections: GeneralSearchSectionOption[],
  lang: string,
  previous: GeneralSearchDocOption[]
): GeneralSearchDocOption[] {
  const compare = comparatorFor(lang);
  const ticked = tickedIdsOf(previous);
  const contentsById: { [id: string]: DocumentArchiveSearchTaxonomyContent } = Object.create(null);
  taxonomyAreas(taxonomy).forEach((area) => {
    const contents = area && Array.isArray(area.contents) ? area.contents : [];
    contents.forEach((content) => {
      if (content && isArchiveObjectId(content._id) && !contentsById[content._id]) {
        contentsById[content._id] = content;
      }
    });
  });

  const seen: { [id: string]: boolean } = Object.create(null);
  const options: GeneralSearchDocOption[] = [];
  (Array.isArray(sections) ? sections : []).forEach((sectionOption) => {
    const content = sectionOption && sectionOption.checked ? contentsById[sectionOption.id] : undefined;
    if (!content) {
      return;
    }
    const documents = Array.isArray(content.documents) ? content.documents : [];
    const docs: GeneralSearchDocOption[] = [];
    documents.forEach((doc) => {
      if (!doc || !isArchiveObjectId(doc._id) || seen[doc._id]) {
        return;
      }
      seen[doc._id] = true;
      docs.push({
        id: doc._id,
        label: nodeLabel(doc, lang),
        parentId: sectionOption.id,
        parentLabel: sectionOption.label,
        mandatory: doc.mandatory === true,
        red: doc.mandatory === true || doc.highlighted === true,
        checked: !!ticked[doc._id],
        visible: true,
      });
    });
    docs.sort((a, b) => compare(a.label, b.label));
    docs.forEach((doc) => options.push(doc));
  });
  return options;
}

/**
 * Apply the two filters of "Documenti" — "Selezionare solo gli obbligatori" and "Cerca documento" —
 * by setting `visible` on each entry.
 *
 * IN PLACE on purpose: the entries are the objects the checkboxes are bound to, and a copy would drop
 * the ticks of the entries hidden for a while. The text is matched literally (escaped), anywhere in
 * the label, case ignored.
 *
 * @since 3.0.0
 *
 * @param docs          The entries of "Documenti"
 * @param mandatoryOnly Whether "Selezionare solo gli obbligatori" is ticked
 * @param text          Text of "Cerca documento"
 */
export function applyGeneralSearchDocFilters(
  docs: GeneralSearchDocOption[],
  mandatoryOnly: boolean,
  text: string
): void {
  const needle = textOf(text).trim();
  const pattern = needle ? new RegExp(escapeArchiveSearchText(needle), "i") : null;
  (Array.isArray(docs) ? docs : []).forEach((doc) => {
    doc.visible = (!mandatoryOnly || doc.mandatory) && (!pattern || pattern.test(doc.label));
  });
}

/**
 * Options grouped under their heading, in the order they come.
 *
 * @since 3.0.0
 *
 * @param options The options, each with the id and the label of its heading
 */
export function groupGeneralSearchOptions<T extends { parentId: string; parentLabel: string }>(
  options: T[]
): Array<GeneralSearchOptionGroup<T>> {
  const groups: Array<GeneralSearchOptionGroup<T>> = [];
  const positionOf: { [id: string]: number } = Object.create(null);
  (Array.isArray(options) ? options : []).forEach((option) => {
    let position = positionOf[option.parentId];
    if (position === undefined) {
      position = groups.length;
      positionOf[option.parentId] = position;
      groups.push({ id: option.parentId, heading: option.parentLabel, options: [] });
    }
    groups[position].options.push(option);
  });
  return groups;
}

/**
 * The ids of a list, distinct and valid, or null when there are more than the back-end accepts.
 *
 * @param ids The ids
 */
function idList(ids: string[]): string[] | null {
  const list = distinct(ids.filter((id) => isArchiveObjectId(id)));
  return list.length > ARCHIVE_SEARCH_MAX_LIST ? null : list;
}

/**
 * The length of the query string the parameters become, as Angular writes it: `key=value` pairs
 * joined by `&`, an array as one pair per value. Measured with `encodeURIComponent`, which escapes a
 * few characters more than Angular does: the estimate errs on the long side.
 *
 * @since 3.0.0
 *
 * @param params The parameters
 */
export function generalSearchQueryLength(params: DocumentArchiveSearchParams): number {
  const pairs: string[] = [];
  Object.keys(params || {}).forEach((key) => {
    const value = (params as { [key: string]: unknown })[key];
    const values = Array.isArray(value) ? value : [value];
    values.forEach((item) => {
      if (item !== undefined && item !== null) {
        pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
      }
    });
  });
  return pairs.join("&").length;
}

/**
 * The query string of `GET /document-archive-docs/search` for what the user chose, or why it is not
 * sent.
 *
 * As few ids as possible, because they travel in the address (@see GENERAL_SEARCH_MAX_QUERY_LENGTH):
 *  - PROPERTIES: the ticked ones among those SHOWN, as in the legacy. `["all"]` when every property
 *    is ticked and neither "Cerca per nome" nor "Cerca per codice" narrows the list; then, for the
 *    platform staff only, the company chosen goes with it, which is what `all` is read against. With
 *    ids the ids are the scope and the back-end ignores the company, so it is not sent;
 *  - AREAS, only with a group chosen: the ticked ones, or every area of the group when none is
 *    ticked — the back-end has no parameter for a group;
 *  - SECTIONS: the ticked ones, omitted when none or all of them are ticked — either way the areas
 *    already say it;
 *  - DOCUMENT TYPES: the ticked ones among those shown, omitted when none is ticked, and when all are
 *    ticked with no filter of the column narrowing them;
 *  - SUPPLIER, DATES, TEXTS: only when given. Nothing is ever sent empty: an empty value is a 400.
 *
 * «Contratti» needs nothing of its own: the id of its area or of its section widens to every contract
 * type on the back-end.
 *
 * @since 3.0.0
 *
 * @param selection What the user chose
 */
export function buildGeneralSearchParams(selection: GeneralSearchSelection): GeneralSearchParamsOutcome {
  const all = Array.isArray(selection.properties) ? selection.properties : [];
  const filterActive = isPropertyFilterActive(selection.propertyNameFilter, selection.propertyCodeFilter);
  const shown = shownPropertyOptions(all, selection.propertyNameFilter, selection.propertyCodeFilter);
  const tickedIds = shown.filter((option) => option.checked).map((option) => option.id);
  if (!tickedIds.length) {
    return { ok: false, reason: "noProperty" };
  }

  const params: DocumentArchiveSearchParams = {};
  // Checked before the ids: "all" is one word whatever the number of properties it stands for
  if (!filterActive && areAllTicked(all)) {
    params.properties = [ARCHIVE_SEARCH_ALL_PROPERTIES];
    if (isGeneralSearchPlatformRole(selection.roleType) && isArchiveObjectId(selection.companyId)) {
      params.company = selection.companyId;
    }
  } else {
    const propertyIds = idList(tickedIds);
    if (!propertyIds) {
      return { ok: false, reason: "tooManyItems" };
    }
    if (!propertyIds.length) {
      return { ok: false, reason: "noProperty" };
    }
    params.properties = propertyIds;
  }

  if (selection.group) {
    const areas = Array.isArray(selection.areas) ? selection.areas : [];
    const tickedAreas = areas.filter((option) => option.checked);
    const areaIds = idList((tickedAreas.length ? tickedAreas : areas).map((option) => option.id));
    if (!areaIds) {
      return { ok: false, reason: "tooManyItems" };
    }
    if (areaIds.length) {
      params.areaIds = areaIds;
    }

    const sections = Array.isArray(selection.sections) ? selection.sections : [];
    const tickedSections = sections.filter((option) => option.checked);
    if (tickedSections.length && tickedSections.length < sections.length) {
      const contentIds = idList(tickedSections.map((option) => option.id));
      if (!contentIds) {
        return { ok: false, reason: "tooManyItems" };
      }
      params.contentIds = contentIds;
    }

    const docs = Array.isArray(selection.docs) ? selection.docs : [];
    const tickedDocs = docs.filter((option) => option.visible && option.checked);
    const wholeList = !selection.docFilterActive && tickedDocs.length === docs.length;
    if (tickedDocs.length && !wholeList) {
      const docIds = idList(tickedDocs.map((option) => option.id));
      if (!docIds) {
        return { ok: false, reason: "tooManyItems" };
      }
      params.documentTemplateDocIds = docIds;
    }
  }

  if (isArchiveObjectId(selection.supplierId)) {
    params.supplierId = selection.supplierId;
  }

  const startDay = archiveSearchDay(selection.dateFrom);
  const endDay = archiveSearchDay(selection.dateTo);
  // Both are `YYYY-MM-DD`: their order as texts is their order on the calendar
  if (startDay && endDay && startDay > endDay) {
    return { ok: false, reason: "dateOrder" };
  }
  if (startDay) {
    params.startDate = startDay;
  }
  if (endDay) {
    params.endDate = endDay;
  }

  const protocol = textOf(selection.protocol).trim();
  const keyword = textOf(selection.keyword).trim();
  if (protocol.length > ARCHIVE_SEARCH_MAX_TEXT || keyword.length > ARCHIVE_SEARCH_MAX_TEXT) {
    return { ok: false, reason: "textTooLong" };
  }
  if (protocol) {
    params.protocollo = protocol;
  }
  if (keyword) {
    params.keyword = keyword;
  }

  if (generalSearchQueryLength(params) > GENERAL_SEARCH_MAX_QUERY_LENGTH) {
    return { ok: false, reason: "tooManyItems" };
  }
  return { ok: true, params };
}

/**
 * Whether a 400 is the refusal of a parameter: that one carries `errors`, keyed by parameter. The
 * only other 400 of the route, too many rows, carries none.
 *
 * @param err The error
 */
function hasValidationErrors(err: object): boolean {
  const body = (err as { error?: unknown }).error;
  if (!body || typeof body !== "object") {
    return false;
  }
  const errors = (body as { errors?: unknown }).errors;
  return !!errors && typeof errors === "object";
}

/**
 * What a failed search asks of the user.
 *
 * `network` is also what a plain `Error` means here: ApiService emits a failure with no status
 * (network down, request blocked) as an Error value, which the service throws back as an error.
 *
 * @since 3.0.0
 *
 * @param err The error of the search
 */
export function generalSearchErrorKind(err: unknown): GeneralSearchErrorKind {
  if (err instanceof Error) {
    return "network";
  }
  if (!err || typeof err !== "object") {
    return "other";
  }
  switch ((err as { status?: unknown }).status) {
    case 0:
      return "network";
    case 400: {
      // The two 400s of the route answer the JSON envelope of the back-end. Anything else, such as
      // the HTML page of a proxy, is not one of them
      const body = (err as { error?: unknown }).error;
      if (!body || typeof body !== "object") {
        return "other";
      }
      return hasValidationErrors(err) ? "invalidParameters" : "tooManyResults";
    }
    case 403:
      return "forbidden";
    case 414:
    case 431:
      return "tooManyItems";
    case 429:
      return "rateLimited";
    default:
      return "other";
  }
}

/**
 * The seconds to wait after a 429: the `Retry-After` of the answer when the browser lets the page
 * read it, GENERAL_SEARCH_DEFAULT_RETRY_SECONDS otherwise. Across origins the browser hands it over
 * only when the API lists it in `Access-Control-Expose-Headers`: the search and the two summaries of
 * the new archive do since EXT-24 (back-end 234e9f63, October 2026), and an API without it keeps the
 * page on the default.
 *
 * @since 3.0.0
 *
 * @param err The error of the search
 */
export function generalSearchRetrySeconds(err: unknown): number {
  const headers =
    err && typeof err === "object" ? (err as { headers?: { get?: unknown } }).headers : undefined;
  if (!headers || typeof headers.get !== "function") {
    return GENERAL_SEARCH_DEFAULT_RETRY_SECONDS;
  }
  const raw = (headers.get as (name: string) => unknown).call(headers, "Retry-After");
  const seconds = typeof raw === "string" && /^\d+$/.test(raw.trim()) ? parseInt(raw.trim(), 10) : NaN;
  return seconds > 0 && seconds <= 3600 ? seconds : GENERAL_SEARCH_DEFAULT_RETRY_SECONDS;
}

/**
 * The rows of the table, from the items of the answer, in the order they came in.
 *
 * @since 3.0.0
 *
 * @param items The items of the answer
 * @param lang  Language of the page
 */
export function generalSearchResultRows(items: unknown, lang: string): GeneralSearchResultRow[] {
  const rows: GeneralSearchResultRow[] = [];
  (Array.isArray(items) ? items : []).forEach((item: unknown) => {
    if (!item || typeof item !== "object" || typeof (item as { _id?: unknown })._id !== "string") {
      return;
    }
    const row = item as DocumentArchiveSearchRow;
    const data: { [key: string]: unknown } = row.data && typeof row.data === "object" ? row.data : {};
    const property = row.property && typeof row.property === "object" ? row.property : null;
    const content = data.contenutoDocumento;

    rows.push({
      row,
      order: rows.length,
      propertyId: property && isArchiveObjectId(property._id) ? property._id : textOf(row.propertyId),
      propertyName: property ? textOf(property.name) : "",
      masterCode: property ? textOf(property.masterCode) : "",
      typeLabel: nodeLabel(row.documentType, lang),
      structure: textOf(row.structureObjectName),
      content: typeof content === "string" || typeof content === "number" ? String(content) : "",
      executor: textOf(row.supplierName),
      executorSupplierId: isArchiveObjectId(data.ragioneSocialeFornitoreId)
        ? data.ragioneSocialeFornitoreId
        : "",
      documentDate: data.dataDocumento,
      documentDay: archiveDocumentDay(data.dataDocumento),
      documentDateNotApplicable: data.dataDocumentochkNA === true,
      expiryDay: archiveDocumentDay(row.dateExpired),
      code: textOf(row.documentCode),
      statusClass: archiveRowStatusClass(row),
    });
  });
  return rows;
}

/**
 * The rows the two buttons of the table leave on screen: "Nascondi ..." on the missing documents
 * (not filed yet, `present: false`) and on the expired ones.
 *
 * @since 3.0.0
 *
 * @param rows        Every row of the answer
 * @param showMissing Whether the missing documents are shown
 * @param showExpired Whether the expired documents are shown
 */
export function visibleGeneralSearchRows<T extends GeneralSearchResultRow>(
  rows: T[],
  showMissing: boolean,
  showExpired: boolean
): T[] {
  return (Array.isArray(rows) ? rows : []).filter(
    (entry) => (showMissing || entry.row.present === true) && (showExpired || entry.row.expired !== true)
  );
}

/**
 * The value a row is sorted on in a column: what the column prints, dates as calendar days.
 *
 * @param entry The row
 * @param key   The column
 */
function sortValueOf(entry: GeneralSearchResultRow, key: string): string {
  switch (key) {
    case "property":
      return entry.propertyName;
    case "masterCode":
      return entry.masterCode;
    case "docType":
      return entry.typeLabel;
    case "structure":
      return entry.structure;
    case "content":
      return entry.content;
    case "reference":
      return entry.executor;
    case "documentDate":
      return entry.documentDay ? entry.documentDay : "";
    case "expirationDate":
      return entry.expiryDay ? entry.expiryDay : "";
    case "code":
      return entry.code;
    default:
      return "";
  }
}

/**
 * The rows in the order of a column.
 *
 * With no direction, or a column it does not know, the order of the back-end comes back: the third
 * click on a header. Rows with the same value keep the order of the back-end between them, whatever
 * the direction. An empty value sorts first going up and last going down.
 *
 * @since 3.0.0
 *
 * @param rows      The rows
 * @param active    The column (@see GeneralSearchSortKey)
 * @param direction "asc", "desc" or "" for none
 * @param lang      Language of the page
 */
export function sortGeneralSearchRows<T extends GeneralSearchResultRow>(
  rows: T[],
  active: string,
  direction: string,
  lang: string
): T[] {
  const list = Array.isArray(rows) ? rows.slice() : [];
  if ((direction !== "asc" && direction !== "desc") || SORT_KEYS.indexOf(active) === -1) {
    return list.sort((a, b) => a.order - b.order);
  }

  const compare = comparatorFor(lang);
  const factor = direction === "asc" ? 1 : -1;
  const keyed = list.map((entry) => ({ entry, value: sortValueOf(entry, active) }));
  keyed.sort((a, b) => {
    const order = a.value === b.value ? 0 : compare(a.value, b.value);
    return order !== 0 ? order * factor : a.entry.order - b.entry.order;
  });
  return keyed.map((item) => item.entry);
}

/**
 * Whether the role acts on the documents beyond the download: not `master` and `collaborator`, as in
 * the legacy page, and not the guests, as on the archive page.
 *
 * @since 3.0.0
 *
 * @param roleType Type of the active role
 */
export function hasGeneralSearchDocumentActions(roleType: string): boolean {
  return !isGeneralSearchDownloadOnlyRole(roleType) && !isGuestRoleType(roleType);
}

/**
 * The entries of the menu of a row: the actions of the legacy page (D2 = A), with the permissions of
 * the new archive.
 *
 *  - `master` and `collaborator` download, and nothing else, as in the legacy page;
 *  - the guests (`temporary`, `shared`) download and open the archive, as on the archive page, where
 *    every other action is hidden from them;
 *  - everybody else: the archive; the data of a filed document; their edit, the delete and the two
 *    contract actions with the rules of the archive page, each asking for the legacy permission AND
 *    the one the back-end enforces (@see canEditArchiveDocumentData, canDeleteArchiveDocument,
 *    canActOnArchiveContract).
 *
 * Every action but "Archivio" needs the document FILED (`present`) and not deleted, as on the archive
 * page: on an obligation still to be filled there is nothing to read, download or cancel. No
 * "Stampa modulo doc": the legacy page has none.
 *
 * @since 3.0.0
 *
 * @param row         The row: of the results, or the same row read in the tree of its property
 * @param roleType    Type of the active role
 * @param permissions Permissions of the active role
 */
export function generalSearchRowMenu(
  row: DocumentArchiveDocListItem | null | undefined,
  roleType: string,
  permissions: string[]
): GeneralSearchRowMenu {
  const none: GeneralSearchRowMenu = {
    archive: false,
    show: false,
    edit: false,
    download: false,
    remove: false,
    cancel: false,
    expire: false,
  };
  if (!row) {
    return none;
  }

  const downloadOnly = isGeneralSearchDownloadOnlyRole(roleType);
  const documentActions = hasGeneralSearchDocumentActions(roleType);
  const filed = row.present === true && row.deleted !== true;
  const granted = Array.isArray(permissions) ? permissions : [];

  return {
    archive: !downloadOnly,
    show: filed && documentActions,
    edit: filed && canEditArchiveDocumentData(documentActions, granted),
    download: filed && archiveAttachedFiles(row).length > 0,
    remove: filed && canDeleteArchiveDocument(documentActions, granted),
    cancel: filed && documentActions && canActOnArchiveContract(row, roleType, granted),
    expire: filed && documentActions && canExpireArchiveContract(row, roleType, granted),
  };
}

/**
 * Whether the menu of a row has any entry: without one the button is not drawn.
 *
 * @since 3.0.0
 *
 * @param menu The entries
 */
export function hasGeneralSearchRowMenu(menu: GeneralSearchRowMenu | null | undefined): boolean {
  return (
    !!menu &&
    (menu.archive || menu.show || menu.edit || menu.download || menu.remove || menu.cancel || menu.expire)
  );
}

/**
 * Whether an action is still in the menu: checked again on the row of the TREE, which is fresher
 * than the row of the results — the document can have expired or been deleted since the search.
 *
 * @since 3.0.0
 *
 * @param menu   The entries
 * @param action The action
 */
export function isGeneralSearchActionAllowed(
  menu: GeneralSearchRowMenu,
  action: GeneralSearchTreeAction
): boolean {
  if (!menu) {
    return false;
  }
  switch (action) {
    case "show":
      return menu.show;
    case "edit":
      return menu.edit;
    case "remove":
      return menu.remove;
    case "cancel":
      return menu.cancel;
    case "expire":
      return menu.expire;
    default:
      return false;
  }
}

/**
 * A calendar day written the Italian way, `DD/MM/YYYY`, as the legacy export writes it.
 *
 * @param day `YYYY-MM-DD`, or null
 */
function italianDay(day: string | null): string | undefined {
  if (!day) {
    return undefined;
  }
  const parts = day.split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : undefined;
}

/**
 * One line of the Excel export: the columns of the legacy export, in its order, and the colour of
 * the row.
 *
 *  1. Immobile, 2. Tipo documento (with the expired tag on a second line), 3. Impianto,
 *  4. Contenuto, 5. Nome esecutore, 6. Data documento, 7. Data scadenza, 8. Data di carico,
 *  9. Codice.
 *
 * The two dates of the document are calendar days, the one the search filters on; "Data di carico" is
 * an instant, written with its time in the clock of the browser, as in the legacy. A cell with no
 * value is `undefined`: an empty cell, not an empty text.
 *
 * @since 3.0.0
 *
 * @param entry The row
 * @param tags  The texts of the tag of an expired document and of a superseded privacy notice
 */
export function generalSearchExcelLine(
  entry: GeneralSearchResultRow,
  tags: { expired: string; superseded: string }
): GeneralSearchExcelLine {
  let type = entry.typeLabel;
  if (type && entry.row.expired) {
    type = `${type}\n${entry.row.privacy ? tags.superseded : tags.expired}`;
  }

  const loaded = textOf(entry.row.loaded);
  const loadedMoment = loaded ? moment(loaded, moment.ISO_8601, true) : null;

  let color: string | null = null;
  if (entry.statusClass === "document-wrapper-text-mandatory-added") {
    color = GENERAL_SEARCH_EXCEL_GREEN;
  } else if (entry.statusClass === "document-wrapper-text-mandatory-missing") {
    color = GENERAL_SEARCH_EXCEL_RED;
  }

  return {
    cells: [
      entry.propertyName,
      type,
      entry.structure,
      entry.content,
      entry.executor,
      italianDay(entry.documentDay),
      italianDay(entry.expiryDay),
      loadedMoment && loadedMoment.isValid() ? loadedMoment.format("DD/MM/YYYY HH:mm:ss") : undefined,
      entry.code,
    ],
    color,
  };
}
